import { useState } from "react";
import { ArrowUpRight, CircleAlert, CircleCheck, LoaderCircle } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@shared/components/ui/hover-card";
import { parseDate, relativeTime } from "@shared/lib/relative-time";
import { hostOf, textFragmentUrl } from "@shared/lib/source-links";
import { cn } from "@shared/lib/utils";
import { SourceFavicon } from "@shared/components/source-favicon";
import { useLocale, useT } from "@shared/client/locale";

type Preview =
  | { phase: "loading" }
  | {
      phase: "done";
      ok: boolean;
      status?: number;
      title?: string | null;
      description?: string | null;
      reason?: string | null;
    };

/** Beaucoup de sites de presse refusent les robots : la page existe, on ne peut pas le vérifier d'ici. */
const BLOCKED_STATUSES = new Set([401, 403, 429]);

/**
 * Un lien vers une source, partout dans l'app : le logo du site et son nom ;
 * au survol, une carte dit ce qu'est la page (titre, phrase citée, page qui
 * répond ou non) sans quitter l'écran ; au clic, la page s'ouvre directement
 * sur la phrase citée, surlignée par le navigateur.
 */
export function SourceAnchor({
  href,
  label,
  quote,
  meta,
  className,
  iconSize = 16,
  ariaLabel,
  children,
}: {
  href: string;
  /** Nom de la source (« Les Echos ») ; à défaut, le domaine. */
  label?: string | null;
  /** Phrase exacte de la page qui fonde le signal. */
  quote?: string | null;
  /** Complément discret affiché dans la carte (date, type de source). */
  meta?: string | null;
  className?: string;
  iconSize?: number;
  /** Nom accessible, quand le lien n'affiche que le logo. */
  ariaLabel?: string;
  /** Remplace le contenu du lien (logo + nom) quand l'écran a sa propre mise en forme. */
  children?: React.ReactNode;
}) {
  const locale = useLocale();
  const copy = useT().leads.source;
  const [preview, setPreview] = useState<Preview | null>(null);
  const [now] = useState(() => Date.now());
  const host = hostOf(href);
  const name = label?.trim() || host || href;
  const target = textFragmentUrl(href, quote);

  function load(open: boolean) {
    if (!open || preview) return;
    setPreview({ phase: "loading" });
    fetch(`/api/link-preview?url=${encodeURIComponent(href)}`)
      .then((response) => response.json())
      .then((data) => setPreview({ phase: "done", ...data }))
      .catch(() => setPreview({ phase: "done", ok: false, reason: copy.checkFailed }));
  }

  const done = preview?.phase === "done" ? preview : null;
  const blocked = Boolean(done && !done.ok && done.status && BLOCKED_STATUSES.has(done.status));
  const date = parseDate(meta);
  const metaLabel = date ? relativeTime(date.time, now, date.dayOnly, locale) : meta;

  // Lien manifestement malformé : rien à ouvrir, on le dit.
  if (!host) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground" title={href}>
        <CircleAlert aria-hidden="true" className="size-3.5" /> {copy.unreadableLink(label || copy.fallbackLabel)}
      </span>
    );
  }

  return (
    <HoverCard closeDelay={120} onOpenChange={load} openDelay={350}>
      <HoverCardTrigger asChild>
        <a
          aria-label={ariaLabel}
          className={cn(
            !children && "inline-flex min-w-0 items-center gap-1.5 text-[12.5px] font-medium text-foreground hover:underline",
            className,
          )}
          href={target}
          rel="noreferrer"
          target="_blank"
        >
          {children ?? (
            <>
              <SourceFavicon size={iconSize} url={href} />
              <span className="truncate">{name}</span>
            </>
          )}
        </a>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="grid w-[340px] gap-2.5 rounded-xl p-4">
        <span className="flex min-w-0 items-center gap-2 text-[12px] text-muted-foreground">
          <SourceFavicon size={14} url={href} />
          <span className="truncate">{[name !== host ? name : null, host, metaLabel].filter(Boolean).join(" · ")}</span>
        </span>
        {(!preview || preview.phase === "loading") && (
          <span className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
            <LoaderCircle aria-hidden="true" className="size-3.5 animate-spin" /> {copy.loading}
          </span>
        )}
        {done?.title && <strong className="text-[13.5px] leading-snug font-semibold">{done.title}</strong>}
        {quote ? (
          <blockquote className="m-0 border-l-2 pl-3 text-[13px] leading-relaxed text-foreground/85">{copy.quoted(quote)}</blockquote>
        ) : (
          done?.description && <p className="m-0 line-clamp-3 text-[12.5px] leading-relaxed text-muted-foreground">{done.description}</p>
        )}
        {done && (
          <span
            className={cn(
              "flex items-center gap-1.5 text-[12px]",
              done.ok || blocked ? "text-muted-foreground" : "text-destructive",
            )}
          >
            {done.ok ? <CircleCheck aria-hidden="true" className="size-3.5" /> : <CircleAlert aria-hidden="true" className="size-3.5" />}
            {done.ok ? copy.pageOk : blocked ? copy.blocked : done.reason || copy.pageDown}
          </span>
        )}
        <a className="inline-flex w-fit items-center gap-1 text-[12.5px] font-medium text-primary hover:underline" href={target} rel="noreferrer" target="_blank">
          {quote ? copy.openOnQuote : copy.openSource} <ArrowUpRight aria-hidden="true" className="size-3.5" />
        </a>
      </HoverCardContent>
    </HoverCard>
  );
}
